import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";
import { BOOKING_URL, NAV_ITEMS } from "@/lib/site";
import { scrollToId } from "@/lib/scroll";
import Logo from "@/components/Logo";

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [active, setActive] = useState("");
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 60);
      let current = "";
      NAV_ITEMS.forEach(({ id }) => {
        if (!id) return;
        const el = document.querySelector(id);
        if (el && el.getBoundingClientRect().top < window.innerHeight * 0.4) current = id;
      });
      setActive(pathname !== "/" ? pathname : current);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, [pathname]);

  const go = ({ id, to }) => {
    setOpen(false);
    if (to) {
      navigate(to);
      return;
    }
    if (pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToId(id), 400);
    } else {
      scrollToId(id);
    }
  };

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-50 transition-colors duration-700 ${
          scrolled ? "border-b border-gold/20 bg-ivory/90 backdrop-blur-md" : "bg-transparent"
        }`}
        data-testid="main-navbar"
      >
        <div className="mx-auto flex max-w-[1500px] items-center justify-between px-6 py-4 lg:px-14">
          <button onClick={() => go({ id: "#top" })} aria-label="Royal Essence — back to top">
            <Logo dark={scrolled} />
          </button>
          <nav className="hidden items-center gap-9 lg:flex" aria-label="Primary">
            {NAV_ITEMS.map((item) => (
              <button
                key={item.label}
                onClick={() => go(item)}
                data-testid={`nav-link-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                className={`group relative flex items-center gap-2 font-sans text-[11px] font-medium uppercase tracking-[0.28em] transition-colors duration-500 ${
                  scrolled ? "text-walnut hover:text-forest" : "text-ivory/85 hover:text-ivory"
                }`}
              >
                <span
                  className={`h-[5px] w-[5px] rounded-full bg-gold transition-opacity duration-500 ${
                    active === (item.id || item.to) ? "opacity-100" : "opacity-0 group-hover:opacity-50"
                  }`}
                />
                {item.label}
              </button>
            ))}
            <a
              href={BOOKING_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-book-button"
              className={`rounded-full border px-7 py-2.5 font-sans text-[11px] font-medium uppercase tracking-[0.28em] transition-colors duration-500 ${
                scrolled
                  ? "border-gold text-walnut hover:bg-gold/15"
                  : "border-gold/80 text-ivory hover:bg-gold/15"
              }`}
            >
              Book
            </a>
          </nav>
          <button
            className={`flex flex-col gap-1.5 lg:hidden ${scrolled ? "text-forest" : "text-ivory"}`}
            onClick={() => setOpen(true)}
            aria-label="Open menu"
            data-testid="mobile-menu-button"
          >
            <span className="h-px w-7 bg-current" />
            <span className="h-px w-7 bg-current" />
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[60] flex flex-col bg-forest px-8 py-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            data-testid="mobile-menu"
          >
            <div className="flex items-center justify-between">
              <Logo />
              <button
                onClick={() => setOpen(false)}
                className="font-sans text-[11px] uppercase tracking-[0.3em] text-ivory"
                aria-label="Close menu"
                data-testid="mobile-menu-close"
              >
                Close
              </button>
            </div>
            <nav className="mt-20 flex flex-col gap-8" aria-label="Mobile">
              {NAV_ITEMS.map((item, i) => (
                <motion.button
                  key={item.label}
                  onClick={() => go(item)}
                  className="flex items-center gap-4 text-left font-serif text-4xl font-light text-ivory"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.15 + i * 0.07, duration: 0.7 }}
                  data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                >
                  <span className="h-[5px] w-[5px] rounded-full bg-gold" />
                  {item.label}
                </motion.button>
              ))}
              <motion.a
                href={BOOKING_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-6 w-max rounded-full border border-gold px-8 py-3 font-sans text-xs uppercase tracking-[0.3em] text-gold"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6 }}
                data-testid="mobile-book-button"
              >
                Book a Session
              </motion.a>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
